import axios from 'axios';
import toast from 'react-hot-toast';
import type { ChangesAgencyFilterCatalog } from '../types/changesAgencyFilter';
import { buildAgencyFilterCatalogFromSourceSummaries } from '../utils/buildAgencyFilterCatalogFromSourceSummaries';
import { filterAndZeroStateSourceSummaries } from '../utils/stateSourceSummaryCatalog';
import {
  type AssuntoStructuredOption,
  buildStructuredAssuntoOptionsFromRaw,
} from '../utils/assuntoDisplay';

/**
 * Security note:
 * - Never hardcode secrets in the bundle.
 * - Dev: baseURL vazio → browser chama :3000 e o setupProxy encaminha ao gateway (sem CORS).
 * - Produção: REACT_APP_API_URL vazio + nginx proxy, ou URL explícita do BFF.
 */
export const API_BASE_URL =
  process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL ?? '');

type JwtPayload = {
  tenant_id?: string | number;
  tenantId?: string | number;
  tenant?: string | number;
  [k: string]: unknown;
};

function safeParseJwtPayload(token: string): JwtPayload | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function getSafeErrorMessage(error: any): string {
  const status = error?.response?.status as number | undefined;
  const code = error?.code as string | undefined;

  if (code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'A requisição demorou demais. Tente novamente em instantes.';
  }
  if (status === 401) return 'Sessão expirada. Faça login novamente.';
  if (status === 403) return 'Você não tem permissão para executar esta ação.';
  if (status && status >= 500) return 'Erro interno do servidor. Tente novamente.';

  const serverMessage = error?.response?.data?.message;
  if (typeof serverMessage === 'string' && process.env.NODE_ENV !== 'production') {
    return serverMessage;
  }

  if (typeof error?.message === 'string' && process.env.NODE_ENV !== 'production') {
    return error.message;
  }

  return 'Não foi possível concluir a operação. Tente novamente.';
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** GET via fetch na mesma origem (:3000 → proxy → gateway). Evita falhas silenciosas do axios/CORS. */
async function fetchFromApi<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
  const url = new URL(path, origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === null || v === undefined || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const res = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 401 && typeof window !== 'undefined') {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.replace(`${origin}/login`);
    throw new Error('Sessão expirada');
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = `${detail} — ${body.detail}`;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      'A API devolveu HTML em vez de JSON. Use http://localhost:3000 com o container legislacao-web-dev (docker compose microservices).'
    );
  }
  return res.json() as Promise<T>;
}

/** POST via fetch na mesma origem (:3000 → proxy → gateway). */
async function postToApi<T>(path: string, body?: unknown): Promise<T> {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
  const url = new URL(path, origin);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && typeof window !== 'undefined') {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.replace(`${origin}/login`);
    throw new Error('Sessão expirada');
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const payload = await res.json();
      if (payload?.detail) {
        detail = typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail);
      } else if (payload?.message) {
        detail = payload.message;
      }
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      'A API devolveu HTML em vez de JSON. Reinicie o web dev (npm start) após mudanças no proxy.'
    );
  }
  return res.json() as Promise<T>;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      // Optional tenant context (backend must enforce tenancy based on auth token)
      const payload = safeParseJwtPayload(token);
      const tenant =
        payload?.tenant_id ?? payload?.tenantId ?? payload?.tenant;
      if (tenant !== undefined && tenant !== null && tenant !== '') {
        config.headers['X-Tenant-Id'] = String(tenant);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/** Evita vários redirects/toasts quando várias chamadas falham ao mesmo tempo (ex.: token expirado). */
let unauthorizedRedirectScheduled = false;

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (!unauthorizedRedirectScheduled) {
        unauthorizedRedirectScheduled = true;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.replace(`${window.location.origin}/login`);
      }
      return Promise.reject(error);
    }
    const skipToast = error.config?.skipErrorToast === true;
    if (!skipToast) {
      toast.error(getSafeErrorMessage(error));
    }
    return Promise.reject(error);
  }
);

// Types
export interface Change {
  id: number;
  legislation_id: number;
  title: string;
  source: string;
  change_type: string;
  detected_at: string;
  diff_summary?: string;
}

export interface Legislation {
  id: number;
  source_id: number;
  number: string;
  year: string;
  title: string;
  summary?: string;
  type?: string;
  created_at_official?: string;
  url?: string;
  dou_ref?: string;
  vigency_status?: string;
  area_tematica?: string;
  link_origem?: string;
  ultima_verificacao?: string;
  scraper_source?: string;
  created_at: string;
  updated_at: string;
}

export interface BotStatus {
  apontamento: {
    status: string;
    ultimo_inicio: string | null;
    ultimo_fim: string | null;
    proxima_execucao: string | null;
    lock_execucao: boolean;
    fonte: string;
  } | null;
  ultima_execucao: {
    status: string;
    inicio: string;
    fim: string | null;
    novos: number;
    atualizados: number;
    erros: number;
  } | null;
}

export interface LegislacaoHistoricoItem {
  id: number;
  campo_alterado: string;
  valor_anterior: string | null;
  valor_novo: string | null;
  data_alteracao: string;
  origem_coleta: string | null;
}

export interface Source {
  id: number;
  key: string;
  name: string;
  base_url?: string;
  is_active?: boolean;
  last_checked?: string;
}

export interface FederalAgency {
  key: string;
  name: string;
  count: number;
  base_url?: string | null;
}

export interface SourceSummary {
  id: number;
  key: string;
  name: string;
  base_url?: string | null;
  level?: string | null;
  uf?: string | null;
  municipality?: string | null;
  is_active: boolean;
  total_normas: number;
  leis: number;
  decretos: number;
  resolucoes: number;
  outras: number;
}

export interface ApiJob {
  key: string;
  name: string;
  description?: string | null;
  schedule: string;
  interval_seconds: number;
  is_active: boolean;
  last_run_at?: string | null;
  last_status?: string | null;
  last_message?: string | null;
}

export interface ApiJobRun {
  id: number;
  started_at: string;
  finished_at?: string | null;
  status: string;
  message?: string | null;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  created_at: string;
}

/** Filtros salvos nas preferências (ex.: agências de interesse) */
export interface PreferenceFilters {
  sources?: string[];
  agency_keys?: string[];
  types?: string[];
  keywords?: string;
}

export interface AgencySelection {
  agency_names: string[];
  agency_keys: string[];
  total_count: number;
  updated_at?: string | null;
}

export interface PreferenceAgencyHistoryItem {
  id: number;
  created_at: string | null;
  changed_by: string;
  added_names: string[];
  removed_names: string[];
  added_keys: string[];
  removed_keys: string[];
  total_count: number;
  agency_names: string[];
  agency_keys: string[];
}

export interface PreferenceHistoryResponse {
  data: PreferenceAgencyHistoryItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface Preference {
  id?: number;
  user_id: number;
  frequency: 'daily' | 'weekly' | 'monthly';
  channels: string[];
  /** Resposta atual do regulatory (`filters` objeto). Legado: string JSON em `filters_json`. */
  filters?: PreferenceFilters | null;
  filters_json?: string | null;
}

export interface DeliveryLog {
  id: number;
  user_id: number;
  channel: string;
  frequency: string;
  status: 'sent' | 'failed';
  detail?: string;
  sent_at: string;
}

export type BotExecutionStatus =
  | 'INICIADO'
  | 'SUCESSO'
  | 'SUCESSO_COM_ALERTAS'
  | 'ERRO'
  | 'TIMEOUT'
  | 'PAINEL_INDISPONIVEL'
  | 'bloqueado';

export interface BotExecution {
  id: number;
  tipo_execucao: string;
  origem_disparo?: string | null;
  data_inicio_busca: string | null;
  data_fim_busca: string | null;
  inicio_execucao: string;
  fim_execucao: string | null;
  status: BotExecutionStatus | string;
  total_processados: number;
  total_novos: number;
  total_atualizados: number;
  total_ignorados: number;
  total_erros: number;
}

export interface BotExecutionsResponse {
  total: number;
  page: number;
  page_size: number;
  items: BotExecution[];
}

export interface BotExecutionDetail extends BotExecution {
  mensagem_erro?: string | null;
  log_execucao?: string | null;
}

export interface RecentChange {
  id: number;
  legislation_id?: number;
  state: string;
  agency: string;
  law: string;
  date: string;
  change: string;
  published_at?: string | null;
  effective_date: string | null;
  title: string;
  summary: string | null;
  url: string | null;
  dou_ref: string | null;
  greening_status?: string | null;
  greening_questions_count?: number;
}

export interface DashboardStats {
  total_changes: number;
  changes_today: number;
  changes_this_month: number;
  /** Pode não existir dependendo da versão do backend */
  changes_this_year?: number;
  changes_kpi_month_label?: string | null;
  changes_kpi_year_label?: string | null;
  /** Quebra Criada/Atualizada/Revogada por período (backend recente) */
  changes_by_type_today?: Record<string, number>;
  changes_by_type_month?: Record<string, number>;
  changes_by_type_year?: Record<string, number>;
  total_norms: number;
  norms_today: number;
  norms_this_month: number;
  norms_this_year: number;
  norms_by_type_today: Record<string, number>;
  norms_by_type_month: Record<string, number>;
  norms_by_type_year: Record<string, number>;
  norms_by_type_total: Record<string, number>;
  active_sources: number;
  total_sources: number;
  active_sources_federal?: number;
  total_sources_federal?: number;
  active_sources_state?: number;
  total_sources_state?: number;
  active_sources_municipal?: number;
  total_sources_municipal?: number;
  changes_by_type: Record<string, number>;
  changes_by_law_type?: Record<string, number>;
  changes_by_agency?: Record<string, number>;
  /** @deprecated use changes_by_agency — mantido para compatibilidade */
  changes_by_source?: Record<string, number>;
  changes_timeline: Array<{
    date: string;
    count: number;
  }>;
  recent_changes: RecentChange[];
  /** Primeira data de publicação no universo federal monitorado (preset TOTAL nos gráficos). */
  publication_date_min?: string | null;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

// API Service
export const apiService = {
  api,  // Include api instance in the service
  // Auth
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  async register(userData: Partial<User> & { password: string }): Promise<{ user: User; token: string }> {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Health
  async getHealth(): Promise<{ status: string; ts: string }> {
    const response = await api.get('/health');
    return response.data;
  },

  // Changes
  async getRecentChanges(days: number = 7): Promise<Change[]> {
    const response = await api.get('/changes/recent', { params: { days } });
    return response.data;
  },

  async getChangesStateFilterOptions(): Promise<string[]> {
    const response = await api.get('/changes/filters/states');
    return response.data;
  },

  async getChangesAgencyFilterOptions(): Promise<ChangesAgencyFilterCatalog> {
    const [federalRes, stateRes, municipalRes] = await Promise.all([
      api.get<SourceSummary[]>('/sources/summary', { params: { scope: 'federal' } }),
      api.get<SourceSummary[]>('/sources/summary', { params: { scope: 'state' } }),
      api.get<SourceSummary[]>('/sources/summary', { params: { scope: 'municipal' } }),
    ]);
    return buildAgencyFilterCatalogFromSourceSummaries(
      federalRes.data,
      filterAndZeroStateSourceSummaries(stateRes.data),
      municipalRes.data
    );
  },

  async getChangesLawTypeFilterOptions(): Promise<string[]> {
    const response = await api.get('/changes/filters/law-types');
    return response.data;
  },

  async getChangesChangeTypeFilterOptions(): Promise<string[]> {
    const response = await api.get('/changes/filters/change-types');
    return response.data;
  },

  async getChangesAssuntoFilterOptions(q?: string): Promise<string[]> {
    return fetchFromApi<string[]>('/changes/filters/assuntos', q?.trim() ? { q: q.trim() } : undefined);
  },

  async getChangesAssuntoStructuredOptions(q?: string): Promise<AssuntoStructuredOption[]> {
    const params = q?.trim() ? { q: q.trim() } : undefined;
    try {
      return await fetchFromApi<AssuntoStructuredOption[]>(
        '/changes/filters/assuntos-structured',
        params,
      );
    } catch {
      const raw = await fetchFromApi<string[]>('/changes/filters/assuntos', params);
      return buildStructuredAssuntoOptionsFromRaw(raw, q);
    }
  },

  async getChanges(filters: {
    search?: string;
    title?: string;
    law_number?: string;
    date_start?: string | null;
    date_end?: string | null;
    published_start?: string | null;
    published_end?: string | null;
    effective_start?: string | null;
    effective_end?: string | null;
    sources?: string;
    agencies?: string;
    change_types?: string;
    states?: string;
    legislation_types?: string;
    assunto?: string;
    q?: string;
    monitored_norm_assunto?: boolean;
    monitored_agency?: boolean;
    greening_qa_only?: boolean;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    data: any[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    return fetchFromApi('/changes', filters as Record<string, unknown>);
  },

  async getChangeById(id: number): Promise<Change> {
    const response = await api.get(`/changes/${id}`);
    return response.data;
  },

  // Legislation
  async getLegislation(filters: {
    search?: string;
    sources?: string[];
    types?: string[];
    date_start?: string | null;
    date_end?: string | null;
    area_tematica?: string;
    vigency_status?: string;
    monitored_only?: boolean;
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    data: Legislation[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    // Convert arrays to comma-separated strings for backend
    const params: any = { ...filters };
    if (params.sources && Array.isArray(params.sources)) {
      params.sources = params.sources.join(',');
    }
    if (params.types && Array.isArray(params.types)) {
      params.types = params.types.join(',');
    }
    const response = await api.get('/legislation', { params });
    return response.data;
  },

  async getLegislationById(id: number): Promise<Legislation> {
    const response = await api.get(`/legislation/${id}`);
    return response.data;
  },

  async getLegislacaoHistorico(legislationId: number): Promise<{
    legislation_id: number;
    historico: LegislacaoHistoricoItem[];
  }> {
    const response = await api.get(`/legislation/${legislationId}/historico`);
    return response.data;
  },

  // Bot Legislação
  async getBotStatus(): Promise<BotStatus> {
    const response = await api.get('/api/bot/status', { skipErrorToast: true });
    return response.data;
  },

  async triggerBotScraping(params: {
    mode?: 'incremental' | 'full' | 'custom';
    start?: string;
    end?: string;
  } = {}): Promise<{ message: string }> {
    const response = await api.post('/api/admin/bot/trigger', params);
    return response.data;
  },

  async getBotExecucoes(page = 1, pageSize = 20): Promise<{
    total: number;
    page: number;
    page_size: number;
    items: any[];
  }> {
    const response = await api.get('/api/admin/bot/execucoes', { params: { page, page_size: pageSize } });
    return response.data;
  },

  // Sources
  async getSources(): Promise<Source[]> {
    const response = await api.get('/sources');
    return response.data;
  },

  async getFederalAgencies(): Promise<FederalAgency[]> {
    const response = await api.get('/sources/federal-agencies');
    return response.data;
  },

  async getSourcesSummary(): Promise<SourceSummary[]> {
    return fetchFromApi<SourceSummary[]>('/sources/summary');
  },

  async getSourcesSummaryByScope(scope: 'federal' | 'state' | 'municipal'): Promise<SourceSummary[]> {
    return fetchFromApi<SourceSummary[]>('/sources/summary', { scope });
  },

  // Jobs / Crons (Admin)
  async listApiJobs(): Promise<ApiJob[]> {
    const response = await api.get('/api-jobs');
    return response.data;
  },

  async toggleApiJob(jobKey: string, isActive: boolean): Promise<ApiJob> {
    const response = await api.patch(`/api-jobs/${jobKey}`, { is_active: isActive });
    return response.data;
  },

  async listApiJobRuns(jobKey: string): Promise<ApiJobRun[]> {
    const response = await api.get(`/api-jobs/${jobKey}/runs`);
    return response.data;
  },

  async runApiJobNow(jobKey: string): Promise<{ ok: boolean; task_id: string; job_key: string }> {
    const response = await api.post(`/api-jobs/${jobKey}/run`);
    return response.data;
  },

  async getAgencyByKey(key: string): Promise<Source | null> {
    try {
      const response = await api.get(`/sources?key=${key}`, { skipErrorToast: true });
      const sources = response.data as Source[];
      return sources.find(source => source.key.toLowerCase() === key.toLowerCase()) || null;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`Failed to fetch agency with key ${key}:`, error);
      }
      return null;
    }
  },

  // Preferences
  async getPreferences(userId: number): Promise<Preference> {
    const response = await api.get(`/preferences/${userId}`, { skipErrorToast: true });
    return response.data;
  },

  /** Payload para salvar: filters é objeto (sources, types, keywords); o backend persiste como JSON. */
  async setPreferences(userId: number, preferences: Omit<Preference, 'id' | 'user_id'> & { filters?: PreferenceFilters }): Promise<Preference> {
    const response = await api.put(`/preferences/${userId}`, preferences);
    return response.data;
  },

  async getPreferenceAgencySelection(userId: number): Promise<AgencySelection> {
    try {
      const response = await api.get(`/preferences/${userId}/agency-selection`, { skipErrorToast: true });
      return response.data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) throw err;
      const prefs = await this.getPreferences(userId);
      const filters = this.parsePreferenceFilters(prefs);
      const names = Array.isArray(filters.sources) ? filters.sources : [];
      const keys = Array.isArray(filters.agency_keys) ? filters.agency_keys : [];
      return {
        agency_names: names,
        agency_keys: keys,
        total_count: names.length,
        updated_at: null,
      };
    }
  },

  async putPreferenceAgencySelection(
    userId: number,
    payload: { agency_names: string[]; agency_keys: string[] },
  ): Promise<AgencySelection> {
    try {
      const response = await api.put(`/preferences/${userId}/agency-selection`, payload);
      return response.data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) throw err;
      const prefs = await this.getPreferences(userId);
      const filters = this.parsePreferenceFilters(prefs);
      await this.setPreferences(userId, {
        frequency: prefs.frequency,
        channels: prefs.channels,
        filters: {
          ...filters,
          sources: payload.agency_names,
          agency_keys: payload.agency_keys,
        },
      });
      return {
        agency_names: payload.agency_names,
        agency_keys: payload.agency_keys,
        total_count: payload.agency_names.length,
        updated_at: new Date().toISOString(),
      };
    }
  },

  parsePreferenceFilters(prefs: Preference): PreferenceFilters {
    if (prefs.filters && typeof prefs.filters === 'object') {
      return prefs.filters;
    }
    if (prefs.filters_json) {
      try {
        const parsed =
          typeof prefs.filters_json === 'string' ? JSON.parse(prefs.filters_json) : prefs.filters_json;
        return typeof parsed === 'object' && parsed ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  },

  async getPreferenceHistory(
    userId: number,
    params: { section?: string; page?: number; page_size?: number } = {},
  ): Promise<PreferenceHistoryResponse> {
    try {
      const response = await api.get(`/preferences/${userId}/history`, {
        params,
        skipErrorToast: true,
      });
      return response.data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return { data: [], total: 0, page: 1, page_size: params.page_size ?? 20, total_pages: 1 };
      }
      throw err;
    }
  },

  // Delivery Logs
  async getDeliveryLogs(filters: {
    user_id?: number;
    channel?: string;
    status?: string;
    date_start?: string | null;
    date_end?: string | null;
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    data: DeliveryLog[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const response = await api.get('/delivery-logs', { params: filters });
    return response.data;
  },

  // Bot (admin)
  async getBotExecutions(params: {
    fonte_url?: string;
    date_start?: string | null;
    date_end?: string | null;
    status?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<BotExecutionsResponse> {
    const response = await api.get('/api/admin/bot/execucoes', { params });
    return response.data;
  },

  async getBotExecution(execucaoId: number): Promise<BotExecutionDetail> {
    const response = await api.get(`/api/admin/bot/execucoes/${execucaoId}`);
    return response.data;
  },

  async triggerBotRun(body: { mode?: 'incremental' | 'full' | 'custom'; start?: string; end?: string } = {}) {
    const response = await api.post('/api/admin/bot/trigger', body);
    return response.data as { message?: string };
  },

  // Dashboard
  async getDashboardStats(
    dateRange?: { start: string | null; end: string | null },
    agencies?: string[],
    context?: {
      assunto?: string | null;
      states?: string;
      legislation_types?: string;
      change_types?: string;
    },
  ): Promise<DashboardStats> {
    const params: Record<string, string> = {};
    if (dateRange?.start) params.date_start = dateRange.start;
    if (dateRange?.end) params.date_end = dateRange.end;
    if (agencies && agencies.length > 0) params.agencies = agencies.join(',');
    if (context?.assunto) params.assunto = context.assunto;
    if (context?.states) params.states = context.states;
    if (context?.legislation_types) params.legislation_types = context.legislation_types;
    if (context?.change_types) params.change_types = context.change_types;

    return fetchFromApi<DashboardStats>('/dashboard/stats', params);
  },

  // Export
  async exportData(format: 'csv' | 'json', filters: any = {}): Promise<Blob> {
    const response = await api.get('/export', {
      params: { format, ...filters },
      responseType: 'blob',
    });
    return response.data;
  },

  // Metrics
  async getMetrics(): Promise<string> {
    const response = await api.get('/metrics');
    return response.data;
  },

  // Email notifications
  async getChangeEmailPreview(changeId: number): Promise<{
    change_id: number;
    is_monitored: boolean;
    subject: string;
    headline: string;
    title: string;
    summary: string;
    document_url: string;
    intro: string;
    marketing_signature: string;
  }> {
    const response = await api.get(`/changes/${changeId}/email-preview`, { skipErrorToast: true });
    return response.data;
  },

  async sendChangeNotificationEmail(emailData: {
    change_id: number;
    to: string | string[];
    subject?: string;
  }): Promise<{ success: boolean; message: string; status?: string }> {
    const recipients = Array.isArray(emailData.to) ? emailData.to : [emailData.to];
    const response = await api.post('/notifications/send-change-email', {
      ...emailData,
      to: recipients,
    });
    return response.data;
  },

  async getInAppNotifications(): Promise<{
    notifications: Array<{
      id: number;
      change_id: number;
      message: string;
      is_monitored: boolean;
      read_at: string | null;
      created_at: string;
      is_read: boolean;
    }>;
    unread_count: number;
    total: number;
  }> {
    const response = await api.get('/notifications');
    return response.data;
  },

  async markInAppNotificationRead(notificationId: number): Promise<{ success: boolean }> {
    const response = await api.post(`/notifications/${notificationId}/read`);
    return response.data;
  },

  async markAllInAppNotificationsRead(): Promise<{ success: boolean }> {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  async getChangeDeliveryLogs(changeId: number): Promise<Array<{
    id: number;
    recipient_email: string | null;
    status: string;
    sent_at: string;
    sent_by?: string;
    trigger?: string | null;
  }>> {
    const response = await api.get(`/changes/${changeId}/delivery-logs`, { skipErrorToast: true });
    return response.data;
  },

  // Legislation Monitoring
  async getMonitoringStatus(legislationId: number): Promise<{
    is_monitored: boolean;
    monitoring_id: number | null;
    started_at: string | null;
    monitored_by_name: string | null;
  }> {
    const response = await api.get(`/legislation/${legislationId}/monitoring/status`);
    return response.data;
  },

  async toggleMonitoring(legislationId: number): Promise<{
    status: 'started' | 'stopped';
    message: string;
    is_monitored: boolean;
    monitoring_id?: number;
    started_at?: string;
  }> {
    const response = await api.post(`/legislation/${legislationId}/monitoring/toggle`);
    return response.data;
  },

  async getMonitoringHistory(legislationId: number): Promise<{
    history: Array<{
      id: number;
      is_active: boolean;
      started_at: string;
      stopped_at: string | null;
      monitored_by_name: string;
      monitored_by_email: string;
    }>;
  }> {
    const response = await api.get(`/legislation/${legislationId}/monitoring/history`);
    return response.data;
  },

  // Agency Monitoring
  async getAgenciesMonitoring(): Promise<{
    agencies: Array<{
      id: string;
      name: string;
      code: string;
      isMonitored: boolean;
      totalLegislations: number;
      monitoredSince?: string;
    }>;
  }> {
    const response = await api.get('/agencies/monitoring');
    return response.data;
  },

  async toggleAgencyMonitoring(
    agencyCode: string,
    agencyName?: string
  ): Promise<{
    status: 'activated' | 'deactivated';
    message: string;
    is_monitored: boolean;
    monitored_since?: string;
  }> {
    const response = await api.post(`/agencies/${agencyCode}/monitoring/toggle`, {
      name: agencyName,
    });
    return response.data;
  },

  async getAgencyMonitoringHistory(agencyCode: string): Promise<{
    agency_code: string;
    agency_name: string;
    is_monitored: boolean;
    history: Array<{
      id: number;
      action: 'activated' | 'deactivated';
      timestamp: string;
      user?: string;
      agency_name?: string;
      entity_label?: string;
      details?: string;
    }>;
  }> {
    const response = await api.get(`/agencies/${agencyCode}/monitoring/history`);
    return response.data;
  },

  async getAgenciesMonitoringHistory(): Promise<{
    agencies: Array<{
      agency_code: string;
      agency_name: string;
      is_monitored: boolean;
      activated_since?: string;
      last_event: {
        action: 'activated' | 'deactivated';
        timestamp: string;
      } | null;
    }>;
    history: Array<{
      id: number;
      action: 'activated' | 'deactivated';
      timestamp: string;
      user?: string;
      agency_code: string;
      agency_name: string;
      entity_label?: string;
      details?: string;
    }>;
  }> {
    return fetchFromApi('/agencies/monitoring/history');
  },

  async deactivateAgencyMonitoring(agencyCode: string): Promise<{
    status: string;
    message: string;
    is_monitored: boolean;
  }> {
    const response = await api.post(`/agencies/${agencyCode}/monitoring/deactivate`);
    return response.data;
  },

  async getAgencyDeliveryLogs(agencyCode: string): Promise<Array<{
    id: number;
    recipient_email: string | null;
    status: string;
    sent_at: string;
    sent_by?: string;
    trigger?: string | null;
  }>> {
    const response = await api.get(`/agencies/${agencyCode}/delivery-logs`, { skipErrorToast: true });
    return response.data;
  },

  async getAgenciesMonitoringDeliveryLogs(): Promise<Array<{
    id: number;
    recipient_email: string | null;
    status: string;
    sent_at: string;
    sent_by?: string;
    trigger?: string | null;
  }>> {
    const response = await api.get('/agencies/monitoring/delivery-logs', { skipErrorToast: true });
    return response.data;
  },

  async getAgencyMonitoredLegislations(agencyCode: string): Promise<{
    agency_code: string;
    agency_name: string;
    total_monitored: number;
    legislations: Array<{
      id: number;
      number: string;
      year: string;
      title: string;
      summary?: string;
      type?: string;
      created_at_official?: string;
      url?: string;
      dou_ref?: string;
      created_at: string;
      updated_at: string;
      monitoring_started_at: string;
    }>;
  }> {
    const response = await api.get(`/agencies/${agencyCode}/monitoring/legislations`);
    return response.data;
  },

  // Assunto Monitoring
  async getAssuntosMonitoring(): Promise<{
    assuntos: Array<{
      assunto_key: string;
      assunto_label: string;
      is_monitored: boolean;
      monitored_since?: string;
    }>;
    total: number;
  }> {
    return fetchFromApi('/changes/monitoring/assuntos');
  },

  async activateAssuntoMonitoring(assunto: string): Promise<{
    status: string;
    message: string;
    is_monitored: boolean;
    assunto_key: string;
    assunto_label: string;
    monitored_since?: string;
  }> {
    return postToApi('/changes/monitoring/assuntos/activate', { assunto });
  },

  async deactivateAssuntoMonitoring(assuntoKey: string): Promise<{
    status: string;
    message: string;
    is_monitored: boolean;
    assunto_key: string;
  }> {
    return postToApi(
      `/changes/monitoring/assuntos/${encodeURIComponent(assuntoKey)}/deactivate`,
    );
  },

  async activateAssuntoMonitoringByKey(assuntoKey: string): Promise<{
    status: string;
    message: string;
    is_monitored: boolean;
    assunto_key: string;
    assunto_label: string;
  }> {
    return postToApi(
      `/changes/monitoring/assuntos/${encodeURIComponent(assuntoKey)}/activate`,
    );
  },

  async getAssuntosMonitoringDeliveryLogs(): Promise<Array<{
    id: number;
    recipient_email: string | null;
    status: string;
    sent_at: string;
    sent_by?: string;
    trigger?: string | null;
  }>> {
    const response = await api.get('/changes/monitoring/assuntos/delivery-logs', { skipErrorToast: true });
    return response.data;
  },

  // Keyword Monitoring
  async getKeywordsMonitoring(): Promise<{
    keywords: Array<{
      keyword_key: string;
      keyword_label: string;
      is_monitored: boolean;
      monitored_since?: string;
    }>;
    total: number;
  }> {
    return fetchFromApi('/changes/monitoring/keywords');
  },

  async activateKeywordMonitoring(keyword: string): Promise<{
    status: string;
    message: string;
    is_monitored: boolean;
    keyword_key: string;
    keyword_label: string;
    monitored_since?: string;
  }> {
    return postToApi('/changes/monitoring/keywords/activate', { keyword });
  },

  async deactivateKeywordMonitoring(keywordKey: string): Promise<{
    status: string;
    message: string;
    is_monitored: boolean;
    keyword_key: string;
  }> {
    return postToApi(
      `/changes/monitoring/keywords/${encodeURIComponent(keywordKey)}/deactivate`,
    );
  },

  async activateKeywordMonitoringByKey(keywordKey: string): Promise<{
    status: string;
    message: string;
    is_monitored: boolean;
    keyword_key: string;
    keyword_label: string;
  }> {
    return postToApi(
      `/changes/monitoring/keywords/${encodeURIComponent(keywordKey)}/activate`,
    );
  },

  async getKeywordsMonitoringHistory(): Promise<{
    history: Array<{
      id: number;
      action: 'activated' | 'deactivated';
      timestamp: string;
      user?: string;
      keyword_label: string;
      keyword_key: string;
      details?: string;
    }>;
    keywords: Array<{
      keyword_key: string;
      keyword_label: string;
      is_monitored: boolean;
      last_event: {
        action: 'activated' | 'deactivated';
        timestamp: string;
        user?: string;
      };
    }>;
  }> {
    return fetchFromApi('/changes/monitoring/keywords/history');
  },

  async getKeywordsMonitoringDeliveryLogs(): Promise<Array<{
    id: number;
    recipient_email: string | null;
    status: string;
    sent_at: string;
    sent_by?: string;
    trigger?: string | null;
  }>> {
    const response = await api.get('/changes/monitoring/keywords/delivery-logs', { skipErrorToast: true });
    return response.data;
  },

  async getAssuntosMonitoringHistory(): Promise<{
    history: Array<{
      id: number;
      action: 'activated' | 'deactivated';
      timestamp: string;
      user?: string;
      assunto_label: string;
      assunto_key: string;
      details?: string;
    }>;
    assuntos: Array<{
      assunto_key: string;
      assunto_label: string;
      is_monitored: boolean;
      last_event: {
        action: 'activated' | 'deactivated';
        timestamp: string;
        user?: string;
      };
    }>;
  }> {
    return fetchFromApi('/changes/monitoring/assuntos/history');
  },

  async getAssuntoMonitoringHistoryByKey(assuntoKey: string): Promise<{
    assunto_key: string;
    assunto_label: string;
    is_monitored: boolean;
    history: Array<{
      id: number;
      action: 'activated' | 'deactivated';
      timestamp: string;
      user?: string;
      assunto_label: string;
      assunto_key: string;
      details?: string;
    }>;
  }> {
    return fetchFromApi(
      `/changes/monitoring/assuntos/${encodeURIComponent(assuntoKey)}/history`,
    );
  },

  // ============================================================================
  // Radar por Cliente (BOT)
  // ============================================================================
  async radarListCompanies(type: 'provider' | 'business_unit'): Promise<Array<{
    id: string;
    name: string;
    cnpj: string;
    type: 'provider' | 'business_unit';
  }>> {
    const response = await api.get('/radar/companies', { params: { type } });
    return response.data;
  },

  async radarListEnvironmentalAgencies(): Promise<Array<{ id: string; name: string; code: string }>> {
    const response = await api.get('/radar/environmental-agencies');
    return response.data;
  },

  async radarListCredentials(agencyId: string): Promise<Array<{
    id: string;
    label: string;
    username_masked?: string;
    last_used_at?: string | null;
  }>> {
    const response = await api.get('/radar/credentials', { params: { agency_id: agencyId } });
    return response.data;
  },

  async radarCreateCredential(
    agencyId: string,
    input: { label: string; username: string; email: string; password: string; notes?: string }
  ): Promise<{ id: string; label: string; username_masked?: string; last_used_at?: string | null }> {
    const response = await api.post('/radar/credentials', { agency_id: agencyId, ...input });
    return response.data;
  },

  async radarStartRun(input: {
    company_type: 'provider' | 'business_unit';
    company_id: string;
    company_name?: string;
    company_cnpj: string;
    agency_id: string;
    agency_name?: string;
    record_type: 'licenca' | 'autorizacao' | 'pedido' | 'protocolo' | 'processo' | 'outro';
    record_number: string;
    credential_id: string;
    credential_label?: string;
    observations?: string;
  }): Promise<any> {
    const response = await api.post('/radar/runs', input);
    return response.data;
  },

  async radarGetRun(runId: string): Promise<any> {
    const response = await api.get(`/radar/runs/${runId}`);
    return response.data;
  },

  async radarListRuns(params: {
    company_id?: string;
    agency_id?: string;
    record_number?: string;
    limit?: number;
  }): Promise<{ runs: any[] }> {
    const response = await api.get('/radar/runs', { params });
    return response.data;
  },

  // ========== API Regulatória (Passo 3 / Fase 6) ==========

  /** Lista documentos (normas) com paginação */
  async getDocuments(params?: { page?: number; page_size?: number }): Promise<{
    data: DocumentRegulatory[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const response = await api.get('/api/documents', { params: params || {} });
    return response.data;
  },

  /** Busca normas com filtros: órgão, estado, tema, data */
  async searchNorms(params: {
    q?: string;
    org?: string;
    state?: string;
    theme?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    data: DocumentRegulatory[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const response = await api.get('/api/norms', { params });
    return response.data;
  },

  /** Timeline de alterações */
  async getTimeline(params?: {
    date_from?: string;
    date_to?: string;
    days?: number;
    limit?: number;
  }): Promise<{
    data: TimelineItem[];
    total: number;
    date_from: string | null;
    date_to: string | null;
  }> {
    const response = await api.get('/api/timeline', { params: params || {} });
    return response.data;
  },

  /** Impacto por cliente */
  async getClientImpacts(clientId: number): Promise<{
    client_id: number;
    client_name: string;
    impacts: ClientImpactItem[];
    total: number;
  }> {
    const response = await api.get(`/api/clients/${clientId}/impacts`);
    return response.data;
  },
};

// Tipos da API regulatória
export interface DocumentRegulatory {
  id: number;
  source_id: number;
  source_key: string;
  source_name: string;
  number: string | null;
  year: string | null;
  title: string | null;
  summary: string | null;
  type: string | null;
  url: string | null;
  published_at: string | null;
  created_at: string;
}

export interface TimelineItem {
  date: string;
  change_id: number;
  legislation_id: number;
  change_type: string;
  title: string;
  source_name: string;
  state: string;
  detected_at: string;
  diff_summary: string | null;
}

export interface ClientImpactItem {
  legal_document_id: number;
  norm_title: string;
  impact_type: string | null;
  impact_score: number | null;
  rationale: string | null;
  recommended_action: string | null;
}

/** Cliente HTTP autenticado (interceptors JWT/tenant). Uso em serviços de domínio (ex.: greeningApi). */
export { api as apiClient };
